import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';

import { handleHttpError } from '../../helpers/handleHttpError';
import {
  ProductGroupsDto,
  RolloutPlanDetailsDto,
  RolloutPlanDTO,
  RolloutPlanEligiblePackageDto,
  RolloutPlanRequestDto,
  RolloutPlanValidateDeviceWhiteListRequestDto
} from '../../infrastructure/backends/rollout-plan.dto';
import { ApiRemoteUpdate } from '../../infrastructure/services/api-remote-update';
import {
  DeviceListErrorResponse,
  RolloutDataStoreType,
  RolloutPlanChangeHistoryDto,
  RolloutPlanStatisticsDto
} from '../../infrastructure/types/rollout-plan.type';

const initialState: RolloutDataStoreType = {
  rolloutPlan: { data: null, loading: false, error: null },
  rolloutPlanDetails: { data: null, loading: false, error: null },
  allRolloutPlanEligiblePackages: { data: null, loading: false, error: null },
  allProductGroups: { data: null, loading: false, error: null },
  uploadRolloutPlan: { data: null, loading: false, error: null },
  updateRolloutPlan: { data: null, loading: false, error: null },
  rolloutPlanChangeHistory: { data: null, loading: false, error: null },
  countersPerTargetProductGroup: { data: null, loading: false, error: null },
  rolloutPlanReadyForReview: { data: null, loading: false, error: null },
  rejectRolloutPlan: { data: null, loading: false, error: null },
  approveRolloutPlan: { data: null, loading: false, error: null },
  validateDeviceWhitelist: { data: null, loading: false, error: null },
  stopRolloutPlan: { data: null, loading: false, error: null }
};

export const useRolloutPlanStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(store => {
    const api = inject(ApiRemoteUpdate);

    return {
      async loadRolloutPlan() {
        patchState(store, state => ({
          rolloutPlan: { ...state.rolloutPlan, loading: true }
        }));

        try {
          const data = await firstValueFrom(api.get<RolloutPlanDTO[]>('/rolloutPlans'));
          patchState(store, state => ({
            rolloutPlan: {
              ...state.rolloutPlan,
              data,
              loading: false,
              error: null
            }
          }));
        } catch (error) {
          patchState(store, state => ({
            rolloutPlan: {
              ...state.rolloutPlan,
              loading: false,
              error: handleHttpError(error, 'Failed to load devices')
            }
          }));
        }
      },
      async loadRolloutPlanDetails(rolloutPlanId: number) {
        patchState(store, state => ({
          rolloutPlanDetails: { ...state.rolloutPlanDetails, loading: true }
        }));
        try {
          const data = await firstValueFrom(
            api.get<RolloutPlanDetailsDto>(`/rolloutPlan/${rolloutPlanId}`)
          );
          patchState(store, state => ({
            rolloutPlanDetails: {
              ...state.rolloutPlanDetails,
              data,
              loading: false,
              error: null
            }
          }));
        } catch (error) {
          patchState(store, state => ({
            rolloutPlanDetails: {
              ...state.rolloutPlanDetails,
              loading: false,
              error: handleHttpError(error, 'Failed to load details of the rollout plan')
            }
          }));
          throw error;
        }
      },
      loadRolloutPlanEligiblePackages: async () => {
        patchState(store, state => ({
          allRolloutPlanEligiblePackages: { ...state.allRolloutPlanEligiblePackages, loading: true }
        }));
        try {
          const rolloutPlaneligiblePackages = await firstValueFrom(
            api.get<RolloutPlanEligiblePackageDto[]>('/v1/rolloutPlan/eligiblePackages')
          );
          patchState(store, state => ({
            allRolloutPlanEligiblePackages: {
              ...state.allRolloutPlanEligiblePackages,
              data: rolloutPlaneligiblePackages,
              loading: false,
              error: null
            }
          }));
        } catch (error) {
          patchState(store, state => ({
            allRolloutPlanEligiblePackages: {
              ...state.allRolloutPlanEligiblePackages,
              loading: false,
              error: handleHttpError(error, 'Failed to load rollout plan eligible packages')
            }
          }));
          throw error;
        }
      },
      async updateRolloutPlanStatus(
        rolloutPlanId: number,
        options: { newStatus: 'Active' | 'Inactive'; refetch?: boolean }
      ) {
        const endpoint = options.newStatus === 'Active' ? 'activate' : 'deactivate';
        patchState(store, state => ({
          rolloutPlanDetails: { ...state.rolloutPlanDetails, loading: true }
        }));
        try {
          await firstValueFrom(api.post(`/rolloutPlan/${rolloutPlanId}/${endpoint}`));
          patchState(store, state => ({
            rolloutPlanDetails: {
              ...state.rolloutPlanDetails,
              loading: false
            }
          }));

          if (options?.refetch) {
            await this.loadRolloutPlanDetails(rolloutPlanId);
            await this.loadRolloutPlanChangeHistory(rolloutPlanId);
          }
        } catch (error) {
          patchState(store, state => ({
            rolloutPlanDetails: {
              ...state.rolloutPlanDetails,
              loading: false,
              error: handleHttpError(error, `Failed to ${endpoint} the rollout plan`)
            }
          }));
          throw error;
        }
      },
      async loadProductGroups() {
        patchState(store, state => ({
          allProductGroups: { ...state.allProductGroups, loading: true }
        }));
        try {
          const productGroups = await firstValueFrom(api.get<ProductGroupsDto[]>('/productGroups'));
          patchState(store, state => ({
            allProductGroups: {
              ...state.allProductGroups,
              data: productGroups,
              loading: false,
              error: null
            }
          }));
        } catch (error) {
          patchState(store, state => ({
            allProductGroups: {
              ...state.allProductGroups,
              loading: false,
              error: handleHttpError(error, 'Failed to load product groups')
            }
          }));
          throw error;
        }
      },
      async createRolloutPlan(
        requestBody: RolloutPlanRequestDto,
        options: { refetchAllPlans: boolean }
      ) {
        patchState(store, state => ({
          uploadRolloutPlan: { ...state.uploadRolloutPlan, loading: true }
        }));
        try {
          const response = await firstValueFrom(
            api.post<RolloutPlanDetailsDto>('/rolloutPlan', requestBody)
          );
          patchState(store, state => ({
            uploadRolloutPlan: {
              ...state.uploadRolloutPlan,
              data: response,
              loading: false,
              error: null
            }
          }));
          if (options.refetchAllPlans) await this.loadRolloutPlan();
        } catch (error) {
          patchState(store, state => ({
            uploadRolloutPlan: {
              ...state.uploadRolloutPlan,
              loading: false,
              error: handleHttpError(error, 'Failed to create rollout plan')
            }
          }));
          throw error;
        }
      },
      async deleteRolloutPlan(rolloutPlanId: number, options?: { refetchAllPlans?: boolean }) {
        patchState(store, state => ({
          rolloutPlanDetails: { ...state.rolloutPlanDetails, loading: true }
        }));
        try {
          await firstValueFrom(api.delete(`/rolloutPlan/${rolloutPlanId}`));
          patchState(store, state => ({
            rolloutPlanDetails: {
              ...state.rolloutPlanDetails,
              loading: false
            }
          }));
          if (options?.refetchAllPlans) await this.loadRolloutPlan();
        } catch (error) {
          patchState(store, state => ({
            rolloutPlanDetails: {
              ...state.rolloutPlanDetails,
              loading: false,
              error: handleHttpError(error, `Failed to delete the rollout plan`)
            }
          }));
          throw error;
        }
      },
      async editRolloutPlan(
        rolloutPlanId: number,
        options: { refetchAllPlans?: boolean; metadata: RolloutPlanRequestDto }
      ) {
        patchState(store, state => ({
          updateRolloutPlan: { ...state.updateRolloutPlan, loading: true }
        }));
        try {
          await firstValueFrom(api.put(`/rolloutPlan/${rolloutPlanId}`, options.metadata));
          patchState(store, state => ({
            updateRolloutPlan: {
              ...state.updateRolloutPlan,
              loading: false
            }
          }));
          if (options.refetchAllPlans) {
            await this.loadRolloutPlan();
            await this.loadRolloutPlanDetails(rolloutPlanId);
            await this.loadRolloutPlanChangeHistory(rolloutPlanId);
          }
        } catch (error) {
          patchState(store, state => ({
            updateRolloutPlan: {
              ...state.updateRolloutPlan,
              loading: false,
              error: handleHttpError(error, `Failed to update the rollout plan`)
            }
          }));
          throw error;
        }
      },
      async loadRolloutPlanChangeHistory(rolloutPlanId: number) {
        patchState(store, state => ({
          rolloutPlanChangeHistory: { ...state.rolloutPlanChangeHistory, loading: true }
        }));
        try {
          const response = await firstValueFrom(
            api.get<RolloutPlanChangeHistoryDto[]>(`/rolloutPlan/${rolloutPlanId}/history`)
          );
          patchState(store, state => ({
            rolloutPlanChangeHistory: {
              ...state.rolloutPlanChangeHistory,
              data: response,
              loading: false
            }
          }));
          return response;
        } catch (error) {
          patchState(store, state => ({
            rolloutPlanChangeHistory: {
              ...state.rolloutPlanChangeHistory,
              loading: false,
              error: handleHttpError(error, 'Failed to load rollout plan change histories')
            }
          }));
          throw error;
        }
      },
      async loadCountersPerTargetProductGroup(rolloutPlanId: number) {
        patchState(store, state => ({
          countersPerTargetProductGroup: { ...state.countersPerTargetProductGroup, loading: true }
        }));

        try {
          const response = await firstValueFrom(
            api.get<RolloutPlanStatisticsDto>(`/rolloutPlan/${rolloutPlanId}/statistics`)
          );
          patchState(store, state => ({
            countersPerTargetProductGroup: {
              ...state.countersPerTargetProductGroup,
              data: response,
              loading: false
            }
          }));
          return response;
        } catch (error) {
          patchState(store, state => ({
            countersPerTargetProductGroup: {
              ...state.countersPerTargetProductGroup,
              loading: false,
              error: handleHttpError(error, 'Failed to load counters per target product group')
            }
          }));
          throw error;
        }
      },
      async markRolloutPlanReadyForReview(rolloutPlanId: number, options: { refetch?: boolean }) {
        patchState(store, state => ({
          rolloutPlanReadyForReview: { ...state.rolloutPlanReadyForReview, loading: true }
        }));
        try {
          await firstValueFrom(api.post(`/rolloutPlan/${rolloutPlanId}/ready`));
          patchState(store, state => ({
            rolloutPlanReadyForReview: {
              ...state.rolloutPlanReadyForReview,
              loading: false
            }
          }));
          if (options?.refetch) {
            await this.loadRolloutPlanDetails(rolloutPlanId);
            await this.loadRolloutPlanChangeHistory(rolloutPlanId);
          }
        } catch (error) {
          patchState(store, state => ({
            rolloutPlanReadyForReview: {
              ...state.rolloutPlanReadyForReview,
              loading: false,
              error: handleHttpError(error, `Failed to make rollout plan ready for review`)
            }
          }));
          throw error;
        }
      },
      async markRolloutPlanRejected(
        rolloutPlanId: number,
        reason: string | null,
        options: { refetch?: boolean }
      ) {
        patchState(store, state => ({
          rejectRolloutPlan: { ...state.rejectRolloutPlan, loading: true }
        }));
        try {
          await firstValueFrom(
            api.post(`/rolloutPlan/${rolloutPlanId}/reject`, { remarks: reason })
          );
          patchState(store, state => ({
            rejectRolloutPlan: {
              ...state.rejectRolloutPlan,
              loading: false
            }
          }));
          if (options?.refetch) {
            await this.loadRolloutPlanDetails(rolloutPlanId);
            await this.loadRolloutPlanChangeHistory(rolloutPlanId);
          }
        } catch (error) {
          patchState(store, state => ({
            rejectRolloutPlan: {
              ...state.rejectRolloutPlan,
              loading: false,
              error: handleHttpError(error, `Failed to reject rollout plan`)
            }
          }));
          throw error;
        }
      },
      async markRolloutPlanApproved(rolloutPlanId: number, options: { refetch?: boolean }) {
        patchState(store, state => ({
          approveRolloutPlan: { ...state.approveRolloutPlan, loading: true }
        }));
        try {
          await firstValueFrom(api.post(`/rolloutPlan/${rolloutPlanId}/approve`));
          patchState(store, state => ({
            approveRolloutPlan: {
              ...state.approveRolloutPlan,
              loading: false
            }
          }));
          if (options?.refetch) {
            await this.loadRolloutPlanDetails(rolloutPlanId);
            await this.loadRolloutPlanChangeHistory(rolloutPlanId);
          }
        } catch (error) {
          patchState(store, state => ({
            approveRolloutPlan: {
              ...state.approveRolloutPlan,
              loading: false,
              error: handleHttpError(error, `Failed to approve rollout plan`)
            }
          }));
          throw error;
        }
      },
      async checkValidityOfDeviceWhitelist(
        requestBody: RolloutPlanValidateDeviceWhiteListRequestDto
      ) {
        patchState(store, state => ({
          validateDeviceWhitelist: { ...state.validateDeviceWhitelist, loading: true }
        }));
        try {
          const response = await firstValueFrom(
            api.post<DeviceListErrorResponse>('/rolloutPlan/validate-device-whitelist', requestBody)
          );
          patchState(store, state => ({
            validateDeviceWhitelist: {
              ...state.validateDeviceWhitelist,
              data: response,
              loading: false,
              error: null
            }
          }));
        } catch (error) {
          patchState(store, state => ({
            validateDeviceWhitelist: {
              ...state.validateDeviceWhitelist,
              loading: false,
              error: handleHttpError(error, 'Failed to validate device whitelist')
            }
          }));
          throw error;
        }
      },
      async stopRollout(rolloutPlanId: number, reason: string, options: { refetch?: boolean }) {
        patchState(store, state => ({
          stopRolloutPlan: { ...state.stopRolloutPlan, loading: true }
        }));
        try {
          await firstValueFrom(api.post(`/rolloutPlan/${rolloutPlanId}/stop`, { remarks: reason }));
          patchState(store, state => ({
            stopRolloutPlan: {
              ...state.stopRolloutPlan,
              loading: false
            }
          }));
          if (options?.refetch) {
            await this.loadRolloutPlanDetails(rolloutPlanId);
            await this.loadRolloutPlanChangeHistory(rolloutPlanId);
          }
        } catch (error) {
          patchState(store, state => ({
            stopRolloutPlan: {
              ...state.stopRolloutPlan,
              loading: false,
              error: handleHttpError(error, `Failed to stop the rollout plan`)
            }
          }));
          throw error;
        }
      }
    };
  })
);
